import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import paths from "../../../utils/constants";
import {
  BreadCrumbSeparatorIcon,
  EarnedBadgeIcon,
  MainBadgeIcon,
  UpcomingBadgeIcon,
} from "./Icons";
import api from "../../../api/api";
import { logMessage } from "../../../utils/helpers";
import { cn } from "../../../lib/utils";

const PURPLE = "#552F83";
const PAGE_BG = "#F1ECFF";
const TRACK_PINK = "#F5D6E4";

const FILTER_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Earned", value: "earned" },
  { label: "Locked", value: "upcoming" },
];

function readFilterFromSearch(search) {
  const tab = new URLSearchParams(search).get("tab");
  if (tab === "earned" || tab === "upcoming" || tab === "all") return tab;
  return "all";
}

function isApiSuccess(res) {
  if (res == null) return false;
  const s = res.status;
  return s === 1 || s === "1" || Number(s) === 1;
}

function isBadgeEarned(badge) {
  if (!badge || typeof badge !== "object") return false;
  const s = String(badge.status || "").toLowerCase();
  if (s === "earned" || s === "completed" || s === "unlocked") return true;
  if (s === "upcoming" || s === "locked") return false;
  if (badge.locked === true || badge.is_locked === true) return false;
  if (badge.unlocked === true || badge.is_unlocked === true) return true;
  return false;
}

function pickCategories(data) {
  if (!data) return [];
  if (Array.isArray(data.categories)) return data.categories;
  if (Array.isArray(data.sections)) return data.sections;
  if (Array.isArray(data.groups)) return data.groups;
  if (Array.isArray(data.badge_categories)) return data.badge_categories;
  return [];
}

function pickBadges(cat) {
  if (!cat) return [];
  if (Array.isArray(cat.badges)) return cat.badges;
  if (Array.isArray(cat.items)) return cat.items;
  if (Array.isArray(cat.milestones)) return cat.milestones;
  return [];
}

function sectionCounts(cat, badges) {
  const earnedFromApi = Number(cat?.earned_count ?? cat?.earned ?? NaN);
  const totalFromApi = Number(cat?.total_count ?? cat?.total ?? NaN);
  if (!Number.isNaN(earnedFromApi) && !Number.isNaN(totalFromApi) && totalFromApi > 0) {
    return { earned: earnedFromApi, total: totalFromApi };
  }
  const earned = badges.filter(isBadgeEarned).length;
  const total = badges.length;
  return { earned, total: total || 0 };
}

function badgeTitle(b) {
  return b?.title || b?.name || b?.label || "Badge";
}

/** Effort: 3 equal columns; other categories: 4 equal columns (each cell 1/3 or 1/4 of row width). */
function categoryGridCols(cat) {
  const key = String(cat?.category || "").toLowerCase();
  if (key === "effort") return 3;
  if (Number(cat?.grid_count) === 3) return 3;
  return 4;
}

const BadgesPanel = ({ startLoading, stopLoading }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [payload, setPayload] = useState(null);
  const [filter, setFilter] = useState(() => readFilterFromSearch(location.search));
  const [loading, setLoading] = useState(true);

  const loadersRef = useRef({ startLoading, stopLoading });
  loadersRef.current = { startLoading, stopLoading };

  useEffect(() => {
    const tab = readFilterFromSearch(location.search);
    setFilter((current) => (current === tab ? current : tab));
  }, [location.search]);

  const load = useCallback(async () => {
    const { startLoading: sl, stopLoading: el } = loadersRef.current;
    setLoading(true);
    sl?.();
    try {
      console.log("Filter", filter);  
      const dashOutcome = await Promise.allSettled([api.getBadges({ filter })]);

      const res = dashOutcome[0].status === "fulfilled" ? dashOutcome[0].value : null;

      let normalized = null;
      if (res && isApiSuccess(res) && res.data) {
        normalized = res.data;
      } else {
        if (dashOutcome[0].status === "rejected") {
          logMessage(dashOutcome[0].reason);
        } else {
          logMessage(`getBadges non-success: ${res?.message || ""}`, "warn");
        }
      }

      setPayload(normalized);
    } catch (e) {
      logMessage(e);
      setPayload(null);
    } finally {
      setLoading(false);
      el?.();
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const totalEarned = Number(payload?.total_earned ?? payload?.earned_count ?? 0);
  const totalBadges = Number(
    payload?.total_badges ?? payload?.badge_total ?? payload?.total_count ?? 0
  );
  const earnedDisplay = String(Math.max(0, totalEarned)).padStart(2, "0");
  const progressPercent =
    totalBadges > 0 ? Math.min(100, Math.round((totalEarned / totalBadges) * 100)) : 0;
  const heroSubtitle =
    payload?.upcoming_text ||
    payload?.subtitle ||
    (totalBadges > 0
      ? `${Math.max(0, totalBadges - totalEarned)} more badges to unlock as you continue painting.`
      : "");

  const categories = useMemo(() => pickCategories(payload || {}), [payload]);

  const sectionBlocks = useMemo(() => {
    return categories
      .map((cat, catIdx) => {
        const badges = pickBadges(cat);
        if (badges.length === 0) return null;
        const { earned, total } = sectionCounts(cat, badges);
        const barPct = total > 0 ? Math.round((earned / total) * 100) : 0;
        const title = cat?.title || cat?.name || cat?.category_name || "Category";
        const subtitle = cat?.sub_title || cat?.subtitle || cat?.description || "";
        const gridCols = categoryGridCols(cat);
        return {
          key: cat?.category || cat?.id || cat?.key || `${title}-${catIdx}`,
          title,
          subtitle,
          earned,
          total,
          barPct,
          badges,
          gridCols,
        };
      })
      .filter(Boolean);
  }, [categories]);

  return (
    <div className="relative min-h-full pb-10" style={{ backgroundColor: PAGE_BG }}>
      <div
        className="relative overflow-hidden pb-28 md:pb-32"
        style={{
          backgroundImage: "url('/bgImage.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 px-4 pt-4">
          <p className="text-[12px] text-white drop-shadow-sm flex items-center flex-wrap gap-0">
            <button
              type="button"
              className="cursor-pointer hover:underline text-white bg-transparent border-0 p-0 font-inherit"
              onClick={() => navigate(paths.YOUR_PROGRESS)}
            >
              Your progress
            </button>
            <BreadCrumbSeparatorIcon className="px-1.5 size-2.5 shrink-0" />
            <span className="font-medium text-white">My Badges</span>
          </p>
        </div>
      </div>

      <div className="relative z-20 px-4 -mt-24 md:-mt-28">
        <div
          className="mx-auto max-w-3xl rounded-[28px] bg-white px-5 py-5 md:px-8 md:py-6 shadow-[0_12px_40px_rgba(85,47,131,0.12)] flex flex-col sm:flex-row items-center gap-5 sm:gap-6"
          style={{ border: "1px solid rgba(85, 47, 131, 0.08)" }}
        >
          <div className="shrink-0 scale-90 sm:scale-100 origin-center">
            <MainBadgeIcon />
          </div>
          <div className="flex-1 w-full min-w-0 flex flex-col gap-3 text-center sm:text-left">
            <h1
              className="text-[26px] md:text-[32px] font-bold leading-tight"
              style={{ color: PURPLE }}
            >
              {earnedDisplay} Earned Badges
            </h1>
            <div className="w-full">
              <div
                className="h-2.5 w-full rounded-full overflow-hidden"
                style={{ backgroundColor: TRACK_PINK }}
              >
                <div
                  className="h-full rounded-full transition-[width] duration-500 ease-out"
                  style={{
                    width: `${progressPercent}%`,
                    backgroundColor: PURPLE,
                  }}
                />
              </div>
            </div>
            {heroSubtitle ? (
              <p className="text-sm text-[#7C7790] font-normal leading-snug">{heroSubtitle}</p>
            ) : null}
          </div>
        </div>

        <div className="mx-auto max-w-3xl flex flex-wrap items-center gap-2.5 mt-6 mb-2">
          {FILTER_OPTIONS.map((item) => {
            const active = filter === item.value;
            return (
              <button
                key={item.value}
                type="button"
                onClick={() => {
                  setFilter(item.value);
                  const params = new URLSearchParams(location.search);
                  if (item.value === "all") {
                    params.delete("tab");
                  } else {
                    params.set("tab", item.value);
                  }
                  const query = params.toString();
                  navigate(
                    query ? `${paths.BADGES_PANEL}?${query}` : paths.BADGES_PANEL,
                    { replace: true },
                  );
                }}
                className={cn(
                  "rounded-full px-5 py-2 text-sm font-semibold transition-colors border-0 cursor-pointer",
                  active
                    ? "text-white shadow-md"
                    : "bg-white text-[#552F83] border border-[#E0D8F1]"
                )}
                style={active ? { backgroundColor: PURPLE } : undefined}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        <div className="mx-auto max-w-3xl flex flex-col gap-10 mt-6">
          {loading ? (
            <p className="text-center text-sm text-[#7C7790] py-8">Loading badges…</p>
          ) : !payload ? (
            <p className="text-center text-sm text-[#7C7790] py-8">Could not load badges. Please try again.</p>
          ) : categories.length === 0 ? (
            <p className="text-center text-sm text-[#7C7790] py-8">No badge categories yet.</p>
          ) : sectionBlocks.length === 0 ? (
            <p className="text-center text-sm text-[#7C7790] py-8">No badges to display.</p>
          ) : (
            sectionBlocks.map((block) => (
              <section key={block.key} className="flex flex-col gap-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h2
                      className="text-lg md:text-xl font-bold leading-tight"
                      style={{ color: PURPLE }}
                    >
                      {block.title}
                    </h2>
                    {block.subtitle ? (
                      <p className="text-sm text-[#7C7790] mt-1">{block.subtitle}</p>
                    ) : null}
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1.5 min-w-[52px]">
                    <span className="text-sm font-semibold tabular-nums" style={{ color: PURPLE }}>
                      {block.earned}/{block.total}
                    </span>
                    <div
                      className="h-1 w-12 rounded-full overflow-hidden"
                      style={{ backgroundColor: TRACK_PINK }}
                    >
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${block.barPct}%`, backgroundColor: PURPLE }}
                      />
                    </div>
                  </div>
                </div>

                <div
                  className={cn(
                    "grid w-full gap-3 md:gap-4 grid-cols-4",
                    // block.gridCols === 3 ? "grid-cols-3" : "grid-cols-4"
                  )}
                >
                  {block.badges.map((badge, idx) => {
                    const earnedBadge = isBadgeEarned(badge);
                    return (
                      <div
                        key={badge?._id || badge?.id || badge?.key || `${badgeTitle(badge)}-${idx}`}
                        className={cn(
                          "rounded-2xl bg-white flex flex-col items-center justify-center px-3 py-4 md:py-5 text-center",
                          "shadow-[0_4px_20px_rgba(85,47,131,0.06)] border border-[#EDE8F7]"
                        )}
                      >
                        <div className="flex items-center justify-center h-[58px]">
                          {earnedBadge ? <EarnedBadgeIcon  /> : <UpcomingBadgeIcon />}
                        </div>
                        <p
                          className={cn(
                            "mt-3 text-xs md:text-[13px] font-medium leading-tight px-0.5",
                            earnedBadge ? "text-[#323232]" : "text-[#B8B4C4]"
                          )}
                        >
                          {badgeTitle(badge)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default BadgesPanel;
